import React, { useCallback, useRef, useState } from 'react';
import {
	IconButton,
	Space,
} from '@components';
import { SummaryBlock, SummaryBlockItem } from '@api';
import {
	TrashIcon,
	PencilIcon,
	CirclePlusIcon,
	DragHandleIcon,
	SummaryLinkIcon
} from '@icons';

import './summary-item-comp.scss';
import { getDateString, useClassNames } from '@common';
import { IDragData, SessionSummaryDragDataKey } from '../summary-schema';
import { TaskLinkComp } from '../task-link';
interface SummaryItemCompProps {
	onSummaryItemAdd: (summaryItemIndex: number, agendaItemId: string) => void,
	onSummaryItemDelete: (summaryItemIndex: number, agendaItemId: string) => void,
	onSummaryItemEdit: (summaryItemIndex: number, agendaItemId: string) => void,
	onHighlightihgChanged: (ids: string[]) => void,
	summaryItem: SummaryBlockItem,
	summaryItemIndex: number,
	summaryBlock: SummaryBlock,
	canEdit: boolean,
}

function SummaryItemComp(props: SummaryItemCompProps): JSX.Element {
	const {
		onSummaryItemDelete,
		onSummaryItemEdit,
		onSummaryItemAdd,
		onHighlightihgChanged,
		summaryItem,
		summaryItemIndex,
		summaryBlock,
		canEdit,
	} = props;

	const [isDragging, setIsDragging] = useState<boolean>(false);

	const onAddSummaryItem = useCallback(() => {
		onSummaryItemAdd(summaryItemIndex + 1, summaryItem.agendaItemId);
	}, [onSummaryItemAdd, summaryItemIndex, summaryItem]);

	const onEditSummaryItem = useCallback(() => {
		onSummaryItemEdit(summaryItemIndex, summaryItem.agendaItemId);
	}, [onSummaryItemEdit, summaryItemIndex, summaryItem]);

	const onDeleteSummaryItem = useCallback(() => {
		onSummaryItemDelete(summaryItemIndex, summaryItem.agendaItemId);
	}, [onSummaryItemDelete, summaryItemIndex, summaryItem]);

	const classes = useClassNames(
		'summary-item',
		isDragging && 'dragging'
	);

	const dragHandleRef = useRef(null);

	let dragTarget = null;
	const onMouseDownHandle = (ev: React.MouseEvent) => {
		if (!canEdit) {
			return;
		}
		dragTarget = ev.target;
	}

	const onDragEndHandle = () => {
		setIsDragging(false);
	}

	const onDragStartHandle = (ev: React.DragEvent) => {
		if (!canEdit) {
			return;
		}

		if (dragHandleRef.current.contains(dragTarget)) {
			sessionStorage.setItem(SessionSummaryDragDataKey, JSON.stringify({
				summaryItemIndex: summaryItemIndex,
				summaryItem: summaryItem,
				summaryBlock: summaryBlock,
				source: 'summary',
			} as IDragData));

			setIsDragging(true);
		} else {
			ev.preventDefault();
		}
	}

	const persons = summaryItem.selectedPersons && summaryItem.selectedPersons.length > 0 && summaryItem.selectedPersons
		.map(p => <span className="person">{p.name}</span>)
		.reduce((acc, p) => acc == null ? p : <>{acc}, {p}</>);

	const dates = summaryItem.selectedDate && getDateString(summaryItem.selectedDate);

	return (
		<div className={classes} 
			draggable={canEdit}
			onMouseDown={onMouseDownHandle}
			onDragStart={onDragStartHandle}
			onDragEnd={onDragEndHandle}
		>
			{summaryItem.transcriptRowIds && (<div className="summary-item__link"><SummaryLinkIcon onClick={() => onHighlightihgChanged(summaryItem.transcriptRowIds)}/></div>)}
			<div ref={dragHandleRef}>
				<DragHandleIcon className='item-handle' />
			</div>
			<div className="summary-item__text">
				{canEdit && summaryItem.isAuto && (<div className="summary-item__auto-tag">Авто</div>)}
				{summaryItem.text}
				{SummeetConfig.showNER && (persons || dates) && <div className="summary-item__entity">
					<div><span className="summary-item__entity-name">Исполнитель:</span> {persons ? persons : "нет исполнителя"}</div>
					<div><span className="summary-item__entity-name">Срок:</span> {dates ? dates : "не установлен"}</div>
				</div>}
				{summaryItem && summaryItem.taskLink && <TaskLinkComp className="summary-item__task-link" taskLink={summaryItem.taskLink} />}
			</div>
			{canEdit && <Space gap="5px" className="summary-item__actions">
				<IconButton
					onClick={onAddSummaryItem}
					icon={<CirclePlusIcon />}
					iconSize="18px"
					size="nogap"
				/>
				<IconButton
					onClick={onEditSummaryItem}
					icon={<PencilIcon />}
					iconSize="18px"
					size="nogap"
				/>
				<IconButton
					onClick={onDeleteSummaryItem}
					icon={<TrashIcon />}
					iconSize="18px"
					size="nogap"
				/>
			</Space>}
		</div>
	);
}

const MemoisedSummaryItemComp = React.memo(SummaryItemComp);

export {MemoisedSummaryItemComp as SummaryItemComp};